import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

// Limites par feature
const LIMITS = {
  chat: { guest: 10, free: 20, pro: Infinity },
  journal: { guest: 0, free: 5, pro: Infinity },
  scanner: { guest: 0, free: 3, pro: Infinity },
  mealplan: { guest: 0, free: 1, pro: Infinity }
};

// Clé pour le mois en cours
const getMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

// Usage stocké en localStorage
const getStoredUsage = () => {
  const monthKey = getMonthKey();
  const stored = localStorage.getItem('nb_usage');
  if (stored) {
    try {
      const data = JSON.parse(stored);
      if (data.month === monthKey) {
        return data;
      }
    } catch (e) {}
  }
  // Nouveau mois, reset
  return { month: monthKey, chat: 0, journal: 0, scanner: 0, mealplan: 0 };
};

const saveStoredUsage = (usage) => {
  localStorage.setItem('nb_usage', JSON.stringify({ ...usage, month: getMonthKey() }));
};

export const useUsageLimits = () => {
  const { isAuthenticated, getSubscriptionStatus } = useAuth();
  const [usage, setUsage] = useState(() => getStoredUsage());

  const subscriptionStatus = getSubscriptionStatus();
  const isPro = subscriptionStatus === 'pro' || subscriptionStatus === 'trial';

  // Déterminer le tier
  const getTier = useCallback(() => {
    if (isPro) return 'pro';
    if (isAuthenticated) return 'free';
    return 'guest';
  }, [isPro, isAuthenticated]);

  // Recharger l'usage au changement de mois ou d'auth
  useEffect(() => {
    const stored = getStoredUsage();
    setUsage(stored);
  }, [isAuthenticated]);

  // Vérifier si une action est autorisée
  const canUse = useCallback((feature) => {
    const tier = getTier();
    const limit = LIMITS[feature]?.[tier] || 0;
    const current = usage[feature] || 0;
    return limit === Infinity || current < limit;
  }, [getTier, usage]);

  // Obtenir les infos de limite
  const getLimit = useCallback((feature) => {
    const tier = getTier();
    const limit = LIMITS[feature]?.[tier] || 0;
    const current = usage[feature] || 0;
    const remaining = limit === Infinity ? Infinity : Math.max(0, limit - current);
    
    return {
      limit,
      used: current,
      remaining,
      isUnlimited: limit === Infinity,
      canUse: limit === Infinity || current < limit
    };
  }, [getTier, usage]);

  // Incrémenter l'usage
  const incrementUsage = useCallback((feature) => {
    const tier = getTier();
    const limit = LIMITS[feature]?.[tier] || 0;
    const current = usage[feature] || 0;

    if (limit !== Infinity && current >= limit) {
      return false;
    }

    const newUsage = { ...usage, [feature]: current + 1 };
    setUsage(newUsage);
    saveStoredUsage(newUsage);
    return true;
  }, [getTier, usage]);

  // Message d'upgrade selon le contexte
  const getUpgradeMessage = useCallback((feature) => {
    const tier = getTier();
    
    if (tier === 'guest') {
      return {
        title: "Inscrivez-vous gratuitement !",
        message: `Vous avez utilisé vos ${LIMITS[feature].guest} ${feature === 'chat' ? 'messages' : 'utilisations'} gratuits ce mois. Inscrivez-vous pour en avoir plus !`,
        action: 'signup',
        buttonText: "S'inscrire gratuitement"
      };
    }
    
    return {
      title: "Passez en Pro ! 🚀",
      message: `Vous avez utilisé vos ${LIMITS[feature].free} ${feature === 'chat' ? 'messages' : 'utilisations'} gratuits ce mois. Passez Pro pour un accès illimité !`,
      action: 'upgrade',
      buttonText: "Débloquer l'illimité"
    };
  }, [getTier]);

  return {
    usage,
    canUse,
    getLimit,
    incrementUsage,
    getUpgradeMessage,
    isPro,
    isAuthenticated,
    tier: getTier()
  };
};

export default useUsageLimits;
